import React, { useEffect, createElement } from 'react';
import { Link } from 'react-router-dom';
import { Footer } from '../components/Footer';
import { SEO } from '../components/SEO';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useLanguage } from '../contexts/LanguageContext';
import { CheckCircleIcon, ClockIcon, VideoIcon, TrendingUpIcon } from 'lucide-react';
export function KostenloseDemoPage() {
  const {
    language
  } = useLanguage();
  // Load Calendly widget
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);
  // Add FAQ Schema for SEO
  useEffect(() => {
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [{
        '@type': 'Question',
        name: language === 'de' ? 'Ist die Demo wirklich kostenlos?' : 'Is the demo really free?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: language === 'de' ? 'Ja, absolut! Die Demo ist 100% kostenlos und unverbindlich. Du erhältst wertvolle Insights und konkrete Tipps, ohne dass du dich zu irgendetwas verpflichten musst.' : 'Yes, absolutely! The demo is 100% free and non-binding. You receive valuable insights and concrete tips without any obligation.'
        }
      }, {
        '@type': 'Question',
        name: language === 'de' ? 'Wie lange dauert die Demo?' : 'How long does the demo take?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: language === 'de' ? 'Die Demo dauert ca. 60 Minuten. In dieser Zeit analysieren wir deine Situation, zeigen dir konkrete Optimierungspotenziale und geben dir sofort umsetzbare Tipps.' : 'The demo takes approximately 60 minutes. During this time, we analyze your situation, show you concrete optimization potential, and give you immediately actionable tips.'
        }
      }, {
        '@type': 'Question',
        name: language === 'de' ? 'Was muss ich vorbereiten?' : 'What do I need to prepare?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: language === 'de' ? 'Nichts! Komm einfach mit deinen Fragen und Herausforderungen. Je mehr du über deine aktuelle Situation erzählst, desto konkreter können wir dir helfen.' : 'Nothing! Just come with your questions and challenges. The more you tell us about your current situation, the more concrete we can help you.'
        }
      }]
    };
    let scriptTag = document.querySelector('script[data-demo-faq-schema]');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.setAttribute('type', 'application/ld+json');
      scriptTag.setAttribute('data-demo-faq-schema', 'true');
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(faqSchema);
    return () => {
      const tag = document.querySelector('script[data-demo-faq-schema]');
      if (tag) tag.remove();
    };
  }, [language]);
  const content = {
    de: {
      title: 'Kostenlose Demo buchen',
      subtitle: 'Entdecke in 60 Minuten, wie CRM Kraft deinen Umsatz verdoppelt',
      benefits: [{
        icon: ClockIcon,
        title: '60 Minuten Analyse',
        description: 'Wir analysieren deine aktuelle Vertriebssituation und zeigen dir konkrete Optimierungspotenziale'
      }, {
        icon: TrendingUpIcon,
        title: 'Sofort umsetzbare Tipps',
        description: 'Du erhältst direkt im Gespräch 3-5 Quick Wins, die du sofort umsetzen kannst'
      }, {
        icon: VideoIcon,
        title: 'Live CRM-Demo',
        description: 'Sieh live, wie ein optimiertes CRM-System funktioniert und deinen Vertrieb transformiert'
      }],
      whatYouGet: 'Was du in der Demo bekommst:',
      checklist: ['Kostenlose Analyse deiner aktuellen Vertriebsprozesse', 'Konkrete Optimierungsvorschläge für dein CRM', '3-5 Quick Wins zum sofort Umsetzen', 'Live-Demo eines optimierten CRM-Systems', 'Individuelle Roadmap für deine Vertriebsoptimierung', 'Keine Verpflichtungen – 100% kostenlos'],
      socialProof: {
        title: 'Über 110 Unternehmen vertrauen uns',
        subtitle: '€7M+ zusätzlicher Umsatz für unsere Kunden generiert'
      },
      urgency: {
        title: 'Limitierte Plätze verfügbar',
        description: 'Wir nehmen nur 10 neue Kunden pro Monat auf, um höchste Qualität zu garantieren.'
      },
      guarantee: {
        title: '100% Zufriedenheitsgarantie',
        description: 'Wenn du nach dem Gespräch nicht mindestens 3 konkrete Optimierungsideen hast, bekommst du eine kostenlose Follow-up Session.'
      }
    },
    en: {
      title: 'Book Free Demo',
      subtitle: 'Discover in 60 minutes how CRM Kraft doubles your revenue',
      benefits: [{
        icon: ClockIcon,
        title: '60 Minute Analysis',
        description: 'We analyze your current sales situation and show you concrete optimization potential'
      }, {
        icon: TrendingUpIcon,
        title: 'Immediately Actionable Tips',
        description: 'You receive 3-5 quick wins directly in the conversation that you can implement immediately'
      }, {
        icon: VideoIcon,
        title: 'Live CRM Demo',
        description: 'See live how an optimized CRM system works and transforms your sales'
      }],
      whatYouGet: 'What you get in the demo:',
      checklist: ['Free analysis of your current sales processes', 'Concrete optimization suggestions for your CRM', '3-5 quick wins to implement immediately', 'Live demo of an optimized CRM system', 'Individual roadmap for your sales optimization', 'No obligations – 100% free'],
      socialProof: {
        title: 'Over 110 companies trust us',
        subtitle: '€7M+ additional revenue generated for our clients'
      },
      urgency: {
        title: 'Limited spots available',
        description: 'We only take 10 new clients per month to guarantee highest quality.'
      },
      guarantee: {
        title: '100% Satisfaction Guarantee',
        description: "If you don't have at least 3 concrete optimization ideas after the call, you get a free follow-up session."
      }
    }
  };
  const t = content[language];
  return <div className="min-h-screen bg-[#0a0e1a]" data-id="element-599">
      <SEO title={language === 'de' ? 'Kostenlose Demo buchen | CRM Kraft' : 'Book Free Demo | CRM Kraft'} description={language === 'de' ? 'Buche jetzt deine kostenlose 60-minütige CRM-Demo. Erhalte sofort umsetzbare Tipps und entdecke, wie du deinen Umsatz verdoppeln kannst.' : 'Book your free 60-minute CRM demo now. Get immediately actionable tips and discover how to double your revenue.'} canonical="https://crmkraft.de/kostenlose-demo" data-id="element-600" />

      {/* Custom Navigation with Language Switcher */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0e1a]/95 backdrop-blur-sm border-b border-white/10" data-id="element-601">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-602">
          <div className="flex items-center justify-between h-20" data-id="element-603">
            <Link to="/" className="flex items-center gap-2" data-id="element-604">
              <span className="text-2xl font-bold text-white" data-id="element-605">
                CRM <span className="text-[#ff6b35]" data-id="element-606">Kraft</span>
              </span>
            </Link>

            <LanguageSwitcher data-id="element-607" />
          </div>
        </div>
      </nav>

      <main className="pt-20" data-id="element-608">
        {/* Hero Section */}
        <section className="px-4 py-16 bg-gradient-to-br from-[#1a1f36] via-[#0a0e1a] to-[#0a0e1a]" data-id="element-609">
          <div className="max-w-4xl mx-auto text-center" data-id="element-610">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-id="element-611">
              {t.title}
            </h1>
            <p className="text-xl sm:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto" data-id="element-612">
              {t.subtitle}
            </p>

            {/* Trust Indicators */}
            <div className="flex flex-wrap justify-center gap-6 mb-8" data-id="element-613">
              <div className="flex items-center gap-2 text-gray-300" data-id="element-614">
                <CheckCircleIcon className="w-5 h-5 text-green-400" data-id="element-615" />
                <span className="text-sm font-medium" data-id="element-616">
                  {language === 'de' ? '100% Kostenlos' : '100% Free'}
                </span>
              </div>
              <div className="flex items-center gap-2 text-gray-300" data-id="element-617">
                <CheckCircleIcon className="w-5 h-5 text-green-400" data-id="element-618" />
                <span className="text-sm font-medium" data-id="element-619">
                  {language === 'de' ? 'Keine Verpflichtungen' : 'No Obligations'}
                </span>
              </div>
              <div className="flex items-center gap-2 text-gray-300" data-id="element-620">
                <CheckCircleIcon className="w-5 h-5 text-green-400" data-id="element-621" />
                <span className="text-sm font-medium" data-id="element-622">
                  {language === 'de' ? '60 Minuten' : '60 Minutes'}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="px-4 py-12 bg-[#0d1225]" data-id="element-623">
          <div className="max-w-6xl mx-auto" data-id="element-624">
            <div className="grid md:grid-cols-3 gap-8" data-id="element-625">
              {t.benefits.map((benefit, index) => <div key={index} className="bg-gradient-to-br from-[#1a1f36] to-[#111827] p-6 rounded-xl border border-white/10" data-id="element-626">
                  <div className="w-12 h-12 bg-[#ff6b35]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-627">
                    <benefit.icon className="w-6 h-6 text-[#ff6b35]" data-id="element-628" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-629">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-630">
                    {benefit.description}
                  </p>
                </div>)}
            </div>
          </div>
        </section>

        {/* Main Content: Calendly + Checklist */}
        <section className="px-4 py-16 bg-[#0a0e1a]" data-id="element-631">
          <div className="max-w-6xl mx-auto" data-id="element-632">
            <div className="grid lg:grid-cols-2 gap-12 items-start" data-id="element-633">
              {/* Left: Calendly Widget */}
              <div className="order-2 lg:order-1" data-id="element-634">
                <div className="bg-white rounded-xl overflow-hidden shadow-2xl" data-id="element-635">
                  <div className="calendly-inline-widget" data-url="https://calendly.com/justus-kraft/crm-automatisierung-demo-website?hide_gdpr_banner=1&primary_color=ff6b35" style={{
                  minWidth: '320px',
                  height: '700px'
                }} data-id="element-636"></div>
                </div>
              </div>

              {/* Right: What You Get */}
              <div className="order-1 lg:order-2 lg:sticky lg:top-24" data-id="element-637">
                <h2 className="text-3xl font-bold text-white mb-6" data-id="element-638">
                  {t.whatYouGet}
                </h2>
                <ul className="space-y-4 mb-8" data-id="element-639">
                  {t.checklist.map((item, index) => <li key={index} className="flex items-start gap-3" data-id="element-640">
                      <CheckCircleIcon className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" data-id="element-641" />
                      <span className="text-gray-300 text-lg" data-id="element-642">{item}</span>
                    </li>)}
                </ul>

                {/* Social Proof */}
                <div className="bg-gradient-to-br from-[#1a1f36] to-[#111827] p-6 rounded-xl border border-white/10 mb-6" data-id="element-643">
                  <h3 className="text-xl font-bold text-white mb-2" data-id="element-644">
                    {t.socialProof.title}
                  </h3>
                  <p className="text-gray-400" data-id="element-645">{t.socialProof.subtitle}</p>
                </div>

                {/* Urgency */}
                <div className="bg-gradient-to-br from-[#ff6b35]/20 to-[#ff6b35]/5 p-6 rounded-xl border border-[#ff6b35]/30 mb-6" data-id="element-646">
                  <h3 className="text-xl font-bold text-white mb-2" data-id="element-647">
                    {t.urgency.title}
                  </h3>
                  <p className="text-gray-300" data-id="element-648">{t.urgency.description}</p>
                </div>

                {/* Guarantee */}
                <div className="bg-gradient-to-br from-green-500/20 to-green-500/5 p-6 rounded-xl border border-green-500/30" data-id="element-649">
                  <h3 className="text-xl font-bold text-white mb-2" data-id="element-650">
                    {t.guarantee.title}
                  </h3>
                  <p className="text-gray-300" data-id="element-651">{t.guarantee.description}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="px-4 py-16 bg-[#0d1225]" data-id="element-652">
          <div className="max-w-4xl mx-auto" data-id="element-653">
            <h2 className="text-3xl font-bold text-white text-center mb-12" data-id="element-654">
              {language === 'de' ? 'Häufig gestellte Fragen' : 'Frequently Asked Questions'}
            </h2>
            <div className="space-y-6" data-id="element-655">
              <div className="bg-gradient-to-br from-[#1a1f36] to-[#111827] p-6 rounded-xl border border-white/10" data-id="element-656">
                <h3 className="text-xl font-bold text-white mb-3" data-id="element-657">
                  {language === 'de' ? 'Ist die Demo wirklich kostenlos?' : 'Is the demo really free?'}
                </h3>
                <p className="text-gray-400" data-id="element-658">
                  {language === 'de' ? 'Ja, absolut! Die Demo ist 100% kostenlos und unverbindlich. Du erhältst wertvolle Insights und konkrete Tipps, ohne dass du dich zu irgendetwas verpflichten musst.' : 'Yes, absolutely! The demo is 100% free and non-binding. You receive valuable insights and concrete tips without any obligation.'}
                </p>
              </div>

              <div className="bg-gradient-to-br from-[#1a1f36] to-[#111827] p-6 rounded-xl border border-white/10" data-id="element-659">
                <h3 className="text-xl font-bold text-white mb-3" data-id="element-660">
                  {language === 'de' ? 'Wie lange dauert die Demo?' : 'How long does the demo take?'}
                </h3>
                <p className="text-gray-400" data-id="element-661">
                  {language === 'de' ? 'Die Demo dauert ca. 60 Minuten. In dieser Zeit analysieren wir deine Situation, zeigen dir konkrete Optimierungspotenziale und geben dir sofort umsetzbare Tipps.' : 'The demo takes approximately 60 minutes. During this time, we analyze your situation, show you concrete optimization potential, and give you immediately actionable tips.'}
                </p>
              </div>

              <div className="bg-gradient-to-br from-[#1a1f36] to-[#111827] p-6 rounded-xl border border-white/10" data-id="element-662">
                <h3 className="text-xl font-bold text-white mb-3" data-id="element-663">
                  {language === 'de' ? 'Was muss ich vorbereiten?' : 'What do I need to prepare?'}
                </h3>
                <p className="text-gray-400" data-id="element-664">
                  {language === 'de' ? 'Nichts! Komm einfach mit deinen Fragen und Herausforderungen. Je mehr du über deine aktuelle Situation erzählst, desto konkreter können wir dir helfen.' : 'Nothing! Just come with your questions and challenges. The more you tell us about your current situation, the more concrete we can help you.'}
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-665" />
    </div>;
}